/**
 * AI assistant — right slide-over panel controller.
 *
 * The panel + launcher live OUTSIDE #main-content (base_office global_modals /
 * topbar) so they survive every hx-boost swap and are wired exactly once.
 * Per the hx-boost-init rule:
 *   - the Escape listener targets `document` → guarded by a module-level flag;
 *   - per-element listeners (launcher, form, …) use per-element JS-property
 *     flags so a (theoretical) re-init never stacks a second handler.
 *
 * All user-visible chrome strings come from the panel's data-* attributes
 * (English fallbacks, real translations injected by {% trans %}); the LLM
 * answer text is returned by the server already in the user's language.
 */
import { getCsrfToken } from '../utils/csrf.js';

let _escBound = false;

// In-session conversational context (no persistence). Replayed to the server
// on each turn so follow-ups ("its address?") resolve; reset by "new chat".
const MAX_HISTORY_TURNS = 8;
// Upper bound for a single answer round-trip. The server enforces its own
// per-call timeout; this is just a client-side net so the UI never hangs
// forever waiting on a dropped connection.
const CLIENT_TIMEOUT_MS = 180000;
let conversation = [];

function pushTurn(role, content) {
  const text = (content || '').trim();
  if (!text) return;
  conversation.push({ role, content: text });
  if (conversation.length > MAX_HISTORY_TURNS) {
    conversation = conversation.slice(-MAX_HISTORY_TURNS);
  }
}

function getRoot() {
  return document.getElementById('assistant-panel-root');
}

function cfg() {
  const root = getRoot();
  if (!root) return {};
  return {
    endpoint: root.dataset.endpoint,
    todayEndpoint: root.dataset.todayEndpoint,
    thinking: root.dataset.i18nThinking || 'Thinking…',
    error: root.dataset.i18nError || 'The assistant is unavailable right now.',
    timeout: root.dataset.i18nTimeout || 'The assistant took too long to respond. Please try again.',
    quota: root.dataset.i18nQuota || 'You have reached your hourly limit.',
    empty: root.dataset.i18nEmpty || 'I could not find that in your data.',
    source: root.dataset.i18nSource || 'Based on your CleanTask data',
    t: {
      title: root.dataset.i18nTodayTitle || 'To review today',
      none: root.dataset.i18nTodayNone || 'Nothing needs your attention today.',
      unassigned: root.dataset.i18nTodayUnassigned || 'visits without a cleaner',
      overdue: root.dataset.i18nTodayOverdue || 'overdue visits',
      running: root.dataset.i18nTodayRunning || 'visits running over time',
      conflicts: root.dataset.i18nTodayConflicts || 'scheduling conflicts',
      absent: root.dataset.i18nTodayAbsent || 'visits with an absent cleaner',
      missing: root.dataset.i18nTodayMissing || 'customers with no visit this week',
      ask: root.dataset.i18nTodayAsk || 'What should I review today?',
    },
  };
}

function renderToday(host, digest, c) {
  host.innerHTML = '';
  const total = digest.total_flags || 0;
  const card = document.createElement('button');
  card.type = 'button';
  card.className =
    `w-full rounded-xl border px-3 py-2.5 text-left transition ${
      total > 0
        ? 'border-amber-200 bg-amber-50 hover:bg-amber-100 dark:border-amber-900/40 dark:bg-amber-900/20'
        : 'border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-700/40'}`;

  const head = document.createElement('div');
  head.className =
    `flex items-center gap-2.5 text-sm font-semibold ${
      total > 0 ? 'text-amber-800 dark:text-amber-300' : 'text-gray-600 dark:text-gray-300'}`;
  if (total > 0) {
    // Same warning-badge treatment as the mobile app's SyncFailedBanner:
    // outline triangle inside a rounded amber square.
    const iconBox = document.createElement('div');
    iconBox.className =
      'inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-amber-100 text-amber-500 dark:bg-amber-900/40 dark:text-amber-400';
    const icon = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    icon.setAttribute('class', 'h-5 w-5');
    icon.setAttribute('fill', 'none');
    icon.setAttribute('stroke', 'currentColor');
    icon.setAttribute('viewBox', '0 0 24 24');
    icon.setAttribute('aria-hidden', 'true');
    icon.innerHTML =
      '<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>';
    iconBox.appendChild(icon);
    head.appendChild(iconBox);
  }
  // No count in the header: the per-category lines below already carry
  // their own numbers, repeating the total here read as a duplicate.
  const headText = document.createElement('span');
  headText.textContent = c.t.title;
  head.appendChild(headText);
  card.appendChild(head);

  if (total === 0) {
    const none = document.createElement('p');
    none.className = 'mt-1 text-xs text-gray-500 dark:text-gray-400';
    none.textContent = c.t.none;
    card.appendChild(none);
  } else {
    const ul = document.createElement('ul');
    // Left padding aligns the lines with the title text (icon box 2rem + gap 0.625rem).
    ul.className = 'mt-1.5 space-y-0.5 pl-[2.625rem] text-xs text-gray-600 dark:text-gray-300';
    const cats = [
      ['unassigned_visits', c.t.unassigned],
      ['overdue_visits', c.t.overdue],
      ['running_over_visits', c.t.running],
      ['cleaner_conflicts', c.t.conflicts],
      ['absent_cleaner_visits', c.t.absent],
      ['customers_no_visit_this_week', c.t.missing],
    ];
    cats.forEach(([key, label]) => {
      const n = (digest[key] && digest[key].count) || 0;
      if (!n) return;
      const li = document.createElement('li');
      li.className = 'flex items-center gap-1.5';
      const badge = document.createElement('span');
      badge.className =
        'inline-flex min-w-[1.25rem] justify-center rounded bg-white px-1 font-semibold text-amber-700 dark:bg-gray-800 dark:text-amber-300';
      badge.textContent = String(n);
      const txt = document.createElement('span');
      txt.textContent = label;
      li.appendChild(badge);
      li.appendChild(txt);
      ul.appendChild(li);
    });
    card.appendChild(ul);
  }

  card.addEventListener('click', () => sendQuestion(c.t.ask));
  host.appendChild(card);
}

async function loadToday() {
  const c = cfg();
  const host = document.getElementById('assistant-today');
  const empty = document.getElementById('assistant-empty-state');
  if (!host || !c.todayEndpoint) return;
  // Only when the empty-state is showing (i.e. no active conversation).
  if (!empty || empty.classList.contains('hidden')) return;
  try {
    const resp = await fetch(c.todayEndpoint, {
      credentials: 'same-origin',
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
    });
    if (!resp.ok) return;
    const data = await resp.json();
    if (!data.enabled || !data.digest) {
      host.innerHTML = '';
      return;
    }
    renderToday(host, data.digest, c);
  } catch {
    // Proactive card is best-effort; never surface an error here.
  }
}

function openDrawer() {
  const drawer = document.getElementById('assistant-drawer');
  const backdrop = document.getElementById('assistant-backdrop');
  if (!drawer) return;
  drawer.classList.remove('translate-x-full');
  drawer.setAttribute('aria-hidden', 'false');
  if (backdrop) backdrop.classList.remove('hidden');
  document.body.classList.add('overflow-hidden');
  const input = document.getElementById('assistant-input');
  if (input) setTimeout(() => input.focus(), 60);
  loadToday();
}

function closeDrawer() {
  const drawer = document.getElementById('assistant-drawer');
  const backdrop = document.getElementById('assistant-backdrop');
  if (!drawer) return;
  drawer.classList.add('translate-x-full');
  drawer.setAttribute('aria-hidden', 'true');
  if (backdrop) backdrop.classList.add('hidden');
  document.body.classList.remove('overflow-hidden');
}

function toggleExpand() {
  const drawer = document.getElementById('assistant-drawer');
  if (!drawer) return;
  drawer.classList.toggle('max-w-md');
  drawer.classList.toggle('max-w-3xl');
}

function clearThread() {
  const thread = document.getElementById('assistant-thread');
  const empty = document.getElementById('assistant-empty-state');
  if (!thread) return;
  // Remove everything except the empty-state node, then reveal it.
  Array.from(thread.children).forEach((child) => {
    if (child !== empty) child.remove();
  });
  if (empty) empty.classList.remove('hidden');
  conversation = [];
  loadToday();
}

function hideEmptyState() {
  const empty = document.getElementById('assistant-empty-state');
  if (empty) empty.classList.add('hidden');
}

function scrollToBottom() {
  const thread = document.getElementById('assistant-thread');
  if (thread) thread.scrollTop = thread.scrollHeight;
}

function makeBubble(role, text) {
  const wrap = document.createElement('div');
  wrap.className = role === 'user' ? 'flex justify-end' : 'flex justify-start';
  const bubble = document.createElement('div');
  bubble.className =
    role === 'user'
      ? 'max-w-[85%] whitespace-pre-wrap rounded-2xl rounded-br-sm bg-primary-600 px-3 py-2 text-sm text-white'
      : 'max-w-[85%] whitespace-pre-wrap rounded-2xl rounded-bl-sm bg-gray-100 px-3 py-2 text-sm text-gray-800 dark:bg-gray-700 dark:text-gray-100';
  bubble.textContent = text;
  wrap.appendChild(bubble);
  return wrap;
}

function appendUser(text) {
  const thread = document.getElementById('assistant-thread');
  if (thread) thread.appendChild(makeBubble('user', text));
}

function appendPending(label) {
  const thread = document.getElementById('assistant-thread');
  if (!thread) return null;
  const node = makeBubble('assistant', label);
  node.dataset.pending = '1';
  node.querySelector('div').classList.add('animate-pulse');
  thread.appendChild(node);
  return node;
}

function appendError(text) {
  const thread = document.getElementById('assistant-thread');
  if (!thread) return;
  const wrap = document.createElement('div');
  wrap.className = 'flex justify-start';
  const bubble = document.createElement('div');
  bubble.className =
    'max-w-[85%] rounded-2xl rounded-bl-sm bg-red-50 px-3 py-2 text-sm text-red-700 dark:bg-red-900/30 dark:text-red-300';
  bubble.textContent = text;
  wrap.appendChild(bubble);
  thread.appendChild(wrap);
}

function buildCitations(citations) {
  const row = document.createElement('div');
  row.className = 'mt-2 flex flex-wrap gap-1.5';
  citations.forEach((c) => {
    const label = c.label || `#${c.id}`;
    let chip;
    if (c.url) {
      chip = document.createElement('a');
      chip.href = c.url;
      chip.setAttribute('hx-boost', 'true');
    } else {
      chip = document.createElement('span');
    }
    chip.className =
      'inline-flex items-center gap-1 rounded-full bg-primary-50 px-2 py-0.5 text-xs font-medium text-primary-700 hover:bg-primary-100 dark:bg-primary-900/30 dark:text-primary-300';
    chip.textContent = label;
    row.appendChild(chip);
  });
  return row;
}

function appendAnswer(data, c) {
  const thread = document.getElementById('assistant-thread');
  if (!thread) return;
  const answerText = (data.answer && data.answer.trim()) || c.empty;
  const node = makeBubble('assistant', answerText);
  const bubble = node.querySelector('div');

  const citations = Array.isArray(data.citations) ? data.citations : [];
  if (citations.length) {
    bubble.appendChild(buildCitations(citations));
    const note = document.createElement('p');
    note.className = 'mt-1.5 text-[11px] italic text-gray-400 dark:text-gray-500';
    note.textContent = c.source;
    bubble.appendChild(note);
  }
  thread.appendChild(node);

  // Let HTMX wire the freshly-injected citation links so clicks boost.
  if (window.htmx && typeof window.htmx.process === 'function') {
    window.htmx.process(node);
  }
}

async function sendQuestion(question) {
  const q = (question || '').trim();
  const c = cfg();
  if (!q || !c.endpoint) return;

  hideEmptyState();
  appendUser(q);
  const pending = appendPending(c.thinking);
  scrollToBottom();

  // Client-side safety net: abort a request that hangs far longer than the
  // server's own per-call timeout, and show the specific "took too long"
  // message instead of a silent failure.
  const controller = new window.AbortController();
  const timer = setTimeout(() => controller.abort(), CLIENT_TIMEOUT_MS);

  try {
    const resp = await fetch(c.endpoint, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRFToken': getCsrfToken(),
      },
      body: JSON.stringify({ question: q, history: conversation.slice() }),
      signal: controller.signal,
    });
    clearTimeout(timer);
    if (pending) pending.remove();

    if (resp.status === 429) {
      appendError(c.quota);
    } else if (!resp.ok) {
      // 504 = timeout, 503 = unavailable; body carries a typed `error`.
      const kind = await readErrorKind(resp);
      appendError(kind === 'timeout' ? c.timeout : c.error);
    } else {
      const data = await resp.json();
      appendAnswer(data, c);
      // Record the exchange only on success so a failed turn doesn't poison
      // the context with a half-finished question.
      pushTurn('user', q);
      pushTurn('assistant', (data.answer && data.answer.trim()) || c.empty);
    }
  } catch (err) {
    clearTimeout(timer);
    if (pending) pending.remove();
    appendError(err && err.name === 'AbortError' ? c.timeout : c.error);
  }
  scrollToBottom();
}

async function readErrorKind(resp) {
  try {
    const data = await resp.json();
    return data && data.error;
  } catch {
    return resp.status === 504 ? 'timeout' : null;
  }
}

function bindOnce(el, flag, type, handler) {
  if (!el || el[flag]) return;
  el[flag] = true;
  el.addEventListener(type, handler);
}

export function initAssistant() {
  const root = getRoot();
  if (!root) return;

  bindOnce(document.getElementById('assistant-launcher'), '_assistantBound', 'click', openDrawer);
  bindOnce(document.getElementById('assistant-close'), '_assistantBound', 'click', closeDrawer);
  bindOnce(document.getElementById('assistant-backdrop'), '_assistantBound', 'click', closeDrawer);
  bindOnce(document.getElementById('assistant-new'), '_assistantBound', 'click', clearThread);
  bindOnce(document.getElementById('assistant-expand'), '_assistantBound', 'click', toggleExpand);

  // Suggested-question chips (delegated on the root so it works after clear).
  bindOnce(root, '_assistantChipsBound', 'click', (e) => {
    const chip = e.target.closest('[data-question]');
    if (!chip) return;
    e.preventDefault();
    sendQuestion(chip.dataset.question);
  });

  const form = document.getElementById('assistant-form');
  bindOnce(form, '_assistantBound', 'submit', (e) => {
    e.preventDefault();
    const input = document.getElementById('assistant-input');
    if (!input) return;
    const q = input.value;
    input.value = '';
    sendQuestion(q);
  });

  // Enter sends, Shift+Enter newlines.
  bindOnce(document.getElementById('assistant-input'), '_assistantBound', 'keydown', (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (form && typeof form.requestSubmit === 'function') form.requestSubmit();
    }
  });

  // Escape closes — document-level → module-level guard (hx-boost-init §1).
  if (!_escBound) {
    _escBound = true;
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') closeDrawer();
    });
  }
}

export function autoInitAssistant() {
  initAssistant();
}
